import React, { useState, useEffect } from "react";
import Header from "./components/Header";
import DangerButton from "./components/DangerButton";
import PrimaryButton from "./components/PrimaryButton";
import { ShoppingBag, CalendarDays, CreditCard, Users, ChevronRight } from "lucide-react";
import BackButton from "./components/BackButton";
import { useRestaurant } from "./context/RestaurantContext.jsx";
import { useUser } from "./context/UserContext.jsx";
import useNav from "./hooks/useNav";
import { toast } from "react-toastify";
import appConfig from "./config";
import { useAuthFetch } from "./hooks/useAuthFetch";

const ORDER_STATUS_AWAITING_PAYMENT = 6;

const orderStatusStyle = (status) => {
    if (status === 4) return "bg-green-100 text-green-700";
    if (status === 3) return "bg-blue-100 text-blue-700";
    if (status === 5) return "bg-red-100 text-red-600";
    if (status === ORDER_STATUS_AWAITING_PAYMENT) return "bg-orange-50 text-orange-500";
    return "bg-gray-100 text-gray-600";
};

const reservationStatusStyle = (status) => {
    if (status === "Подтверждено") return "bg-green-100 text-green-700";
    if (status === "Отменено") return "bg-red-100 text-red-600";
    return "bg-yellow-100 text-yellow-700";
};

const OrdersHistory = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const { restaurant } = useRestaurant();
    const { isAuthenticated } = useUser();
    const navigate = useNav();
    const { authFetch } = useAuthFetch();

    useEffect(() => {
        if (!restaurant?.id || !isAuthenticated) return;
        setLoading(true);
        Promise.all([
            authFetch(`${appConfig.endpoint}/reservations/list?restaurant_id=${restaurant.id}`)
                .then(data => (Array.isArray(data?.reservations) ? data.reservations : []).map(r => ({ ...r, _type: "reservation" })))
                .catch(() => []),
            authFetch(`${appConfig.endpoint}/orders/list?restaurant_id=${restaurant.id}`)
                .then(data => (Array.isArray(data?.orders) ? data.orders : []).map(o => ({ ...o, _type: "order" })))
                .catch(() => []),
        ]).then(([reservations, orders]) => {
            const merged = [...reservations, ...orders].sort((a, b) => {
                const dateA = a._type === "reservation" ? `${a.date} ${a.time}` : a.date;
                const dateB = b._type === "reservation" ? `${b.date} ${b.time}` : b.date;
                return dateB.localeCompare(dateA);
            });
            setItems(merged);
        }).finally(() => setLoading(false));
    }, [restaurant?.id, isAuthenticated]);

    const cancelReservation = async (e, reservationId) => {
        e.stopPropagation();
        try {
            const data = await authFetch(
                `${appConfig.endpoint}/reservations/${reservationId}/cancel`,
                { method: "POST" }
            );
            if (data.success) {
                setItems(prev =>
                    prev.map(item =>
                        item._type === "reservation" && item.id === reservationId
                            ? { ...item, status: "Отменено" }
                            : item
                    )
                );
                toast.success("Бронирование успешно отменено");
            } else {
                toast.error(data.message || "Ошибка при отмене бронирования");
            }
        } catch {
            toast.error("Произошла ошибка. Попробуйте снова.");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <div className="flex items-center gap-3 px-4 py-4 bg-white border-b border-gray-100">
                <BackButton onClick={() => window.history.back()} />
                <h1 className="text-lg font-semibold text-gray-800">Мои брони и заказы</h1>
            </div>

            <div className="max-w-screen-sm mx-auto px-4 pt-4 pb-24 space-y-3">

                {loading && (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brandBlue" />
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                            <ShoppingBag size={28} className="text-gray-300" />
                        </div>
                        <p className="text-base font-medium text-gray-500">Пока ничего нет</p>
                        <p className="text-sm text-gray-400 mt-1">Здесь появятся ваши заказы и бронирования</p>
                    </div>
                )}

                {!loading && items.map((item) =>
                    item._type === "reservation" ? (
                        <div
                            key={`res-${item.id}`}
                            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                                        <CalendarDays size={15} className="text-purple-500" />
                                    </div>
                                    <span className="text-sm font-semibold text-gray-800">Бронирование</span>
                                </div>
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${reservationStatusStyle(item.status)}`}>
                                    {item.status}
                                </span>
                            </div>

                            {/* Date/time */}
                            <p className="text-base font-bold text-gray-900 mb-1">
                                {item.date} в {item.time}
                            </p>

                            {/* People */}
                            <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
                                <Users size={13} className="shrink-0" />
                                <span>{item.people_count} {item.people_count === 1 ? "персона" : item.people_count < 5 ? "персоны" : "персон"}</span>
                            </div>

                            {item.comment && (
                                <p className="text-xs text-gray-400 mb-3 border-t border-gray-50 pt-2">{item.comment}</p>
                            )}

                            {item.status !== "Отменено" && item.status !== "Завершено" && (
                                <div className="flex flex-col gap-2 pt-1">
                                    <div className="flex gap-2">
                                        <PrimaryButton
                                            className="flex-1 py-2.5 text-sm"
                                            onClick={() => navigate(`/reservation/${item.id}`)}
                                        >
                                            Изменить
                                        </PrimaryButton>
                                        <DangerButton
                                            className="flex-1 py-2.5 text-sm"
                                            onClick={(e) => cancelReservation(e, item.id)}
                                        >
                                            Отменить
                                        </DangerButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            key={`order-${item.id}`}
                            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 active:scale-[0.99] transition-transform cursor-pointer"
                            onClick={() => navigate(`/order/${item.id}`)}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center shrink-0">
                                        <ShoppingBag size={15} className="text-orange-400" />
                                    </div>
                                    <span className="text-sm font-semibold text-gray-800">Заказ <span className="text-gray-400 font-normal">#{item.id}</span></span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${orderStatusStyle(item.status)}`}>
                                        {item.statusLabel}
                                    </span>
                                    <ChevronRight size={16} className="text-gray-300" />
                                </div>
                            </div>

                            {/* Amount + date + details link */}
                            <div className="flex items-end justify-between">
                                <div>
                                    <p className="text-xl font-bold text-brandBlue mb-1">{item.total} ₽</p>
                                    <p className="text-xs text-gray-400">{item.date}</p>
                                </div>
                                <span className="text-xs text-brandBlue/70 font-medium pb-0.5">Подробнее →</span>
                            </div>

                            {/* Pay button */}
                            {item.status === ORDER_STATUS_AWAITING_PAYMENT && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/payment-processing?slug=${restaurant?.slug || ""}`, {
                                            state: {
                                                order: { id: item.id, amount: item.total, restaurantID: item.restaurantID },
                                                paymentMethod: "card",
                                                restaurantId: item.restaurantID,
                                            },
                                        });
                                    }}
                                    className="mt-3 w-full flex items-center justify-center gap-2 bg-brandBlue text-white py-2.5 px-4 rounded-xl font-medium text-sm active:scale-95 transition-all"
                                >
                                    <CreditCard size={16} />
                                    Оплатить
                                </button>
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default OrdersHistory;